import React, { ComponentType } from 'react';
import { MultiLineStringInput, StringInput, StringPreview } from '../primitive/StringInput';
import { observer } from 'mobx-react';
import { SelectInput, SelectOptionSpec } from '../primitive/SelectInput';
import './index.module.scss';
import { FieldStateV3 } from '../../../../states/form/FormStateV3';
import { UserFileToken } from '../../../../../mainv3/types/uploadedFiles';
import { UserFileUploadInput } from '../../../../../mainv3/components/organisms/userFiles/UserFileUploadInput';
import { UploadedFilePreview } from '../../../../../mainv3/components/organisms/userFiles/UploadedFilePreview';

type FormLabelProps = {
    children: any;
    styleName?: string;
    className?: string;
};

export const FormLabel = ({ children, className = undefined }: FormLabelProps) => (
    <div styleName="form-label" className={className}>
        {children}
    </div>
);

const FormGroup = ({ id = null, label, children }) => (
    <div id={id} styleName="form-group">
        <FormLabel>{label}</FormLabel>
        <div styleName="form-group-body">{children}</div>
    </div>
);

export const FormError = observer(({ state }) => {
    if (!state.hasErrors()) {
        return null;
    }
    return (
        <div className="form-error" styleName="form-error">
            {state.getErrors()[0]}
        </div>
    );
});

type StringInputProps = {
    id?: string;
    label: string;
    type?: 'text' | 'password';
    size?: 'm' | 'l';
    placeholder?: string;
    state: FieldStateV3<string>;
};
export const VerticalStringInput = observer(
    ({ id, label, type = 'text', size = 'm', placeholder = undefined, state }: StringInputProps) => (
        <FormGroup id={`${state.id}_group`} label={label}>
            <StringInput
                id={id || state.id}
                styleName="input"
                value={state.getDirtyValue()}
                error={state.hasErrors()}
                size={size}
                type={type}
                placeholder={placeholder}
                onChange={state.setDirtyValue}
                onBlur={state.validate}
            />
            <FormError state={state} />
        </FormGroup>
    )
);
const VerticalStringPreview = observer(({ label, type = 'text', size = 'm', state }: StringInputProps) => (
    <FormGroup label={label}>
        <StringPreview size={size}>
            {type === 'text' ? state.getDirtyValue() : (state.getDirtyValue() || '').replace(/./g, '*')}
        </StringPreview>
    </FormGroup>
));

type MultiLineInputProps = {
    id?: string;
    label: string;
    type?: 'text' | 'password';
    size?: 'm' | 'l';
    placeholder?: string;
    state: FieldStateV3<string>;
};
const VerticalMultiLineStringInput = observer(
    ({ id, label, size = 'm', placeholder = undefined, state }: MultiLineInputProps) => (
        <FormGroup id={`${state.id}_group`} label={label}>
            <MultiLineStringInput
                id={id || state.id}
                styleName="input multiline"
                value={state.getDirtyValue()}
                error={state.hasErrors()}
                size={size}
                placeholder={placeholder}
                onChange={state.setDirtyValue}
                onBlur={state.validate}
            />
            <FormError state={state} />
        </FormGroup>
    )
);

type FileInputProps = {
    label: string;
    state: FieldStateV3<UserFileToken[]>;
};
const VerticalFileInput = observer(({ label, state }: FileInputProps) => (
    <FormGroup id={`${state.id}_group`} label={label}>
        <UserFileUploadInput id={state.id} value={state.getDirtyValue()} onChange={state.setDirtyValue} />
        <FormError state={state} />
    </FormGroup>
));

const VerticalFilePreview = observer(({ label, state }: FileInputProps) => {
    return (
        <FormGroup label={label}>
            <UploadedFilePreview value={state.getDirtyValue()} />
        </FormGroup>
    );
});

type SelectInputProps<V> = {
    id?: string;
    label: string;
    size?: 'm' | 'l';
    placeholder?: string;
    options: SelectOptionSpec<V>[];
    serializer: (v: V) => string;
    deserializer: (v: string) => V;
    state: FieldStateV3<V>;
    className?: string;
    styleName?: string;
};
export const VerticalSelectInput = observer(
    ({
        id,
        label,
        size = 'm',
        placeholder = undefined,
        options,
        serializer,
        deserializer,
        state,
        className,
    }: SelectInputProps<any>) => (
        <FormGroup label={label}>
            <SelectInput
                id={id}
                styleName="input"
                value={state.getDirtyValue()}
                error={state.hasErrors()}
                placeholder={placeholder}
                size={size}
                options={options}
                serializer={serializer}
                deserializer={deserializer}
                onChange={state.setDirtyValue}
                onBlur={state.validate}
                className={className}
            />
            <FormError state={state} />
        </FormGroup>
    )
);

export type VerticalFormsType = {
    StringInput: ComponentType<StringInputProps>;
    MultiLineStringInput: ComponentType<MultiLineInputProps>;
    SelectInput: ComponentType<SelectInputProps<any>>;
    StringPreview: ComponentType<StringInputProps>;
    FileInput: ComponentType<FileInputProps>;
    FilePreview: ComponentType<FileInputProps>;
};

export const VerticalForms: VerticalFormsType = {
    StringInput: VerticalStringInput,
    MultiLineStringInput: VerticalMultiLineStringInput,
    SelectInput: VerticalSelectInput,
    StringPreview: VerticalStringPreview,
    FileInput: VerticalFileInput,
    FilePreview: VerticalFilePreview,
};
